/**
 * Cart Rules System
 * Type-safe, composable rules for cart behavior
 */

import type { ShopifyCart, ShopifyLineItem } from '../../types/shopify';

export interface RuleContext {
  cart: ShopifyCart;
  settings: Record<string, any>;
}

export interface RuleResult {
  shouldApply: boolean;
  message?: string;
  action?: () => Promise<void>;
}

export interface CartRule {
  id: string;
  name: string;
  evaluate: (context: RuleContext) => RuleResult | Promise<RuleResult>;
}

/**
 * Gift with Purchase Rule
 * Adds a free gift when cart subtotal reaches threshold
 */
export class GiftWithPurchaseRule implements CartRule {
  id = 'gift-with-purchase';
  name = 'Gift with Purchase';

  constructor(
    private threshold: number,
    private giftVariantId: number
  ) {}

  async evaluate(context: RuleContext): Promise<RuleResult> {
    const { cart } = context;
    const hasGift = cart.items.some(
      (item) => item.variant_id === this.giftVariantId
    );

    const qualifies = cart.total_price >= this.threshold * 100;

    if (qualifies && !hasGift) {
      return {
        shouldApply: true,
        message: `Free gift added! Spend $${(this.threshold / 100).toFixed(2)} to keep it.`,
        action: async () => {
          // Add gift to cart (implementation in CartDrawer)
        },
      };
    }

    if (!qualifies && hasGift) {
      return {
        shouldApply: false,
        message: `Add $${(
          (this.threshold - cart.total_price / 100) / 100
        ).toFixed(2)} more to get a free gift!`,
        action: async () => {
          // Remove gift from cart
        },
      };
    }

    return {
      shouldApply: qualifies,
      message: qualifies
        ? 'Free gift included!'
        : `Add $${((this.threshold - cart.total_price / 100) / 100).toFixed(
            2
          )} more to get a free gift!`,
    };
  }
}

/**
 * Bundle Recommendation Rule
 * Suggests complementary products
 */
export interface BundleConfig {
  triggerProductId: number;
  recommendedProductIds: number[];
  message: string;
}

export class BundleRecommendationRule implements CartRule {
  id = 'bundle-recommendation';
  name = 'Bundle Recommendation';

  constructor(private bundles: BundleConfig[]) {}

  evaluate(context: RuleContext): RuleResult {
    const { cart } = context;

    for (const bundle of this.bundles) {
      const hasTriggerProduct = cart.items.some(
        (item) => item.product_id === bundle.triggerProductId
      );

      if (hasTriggerProduct) {
        const hasRecommended = bundle.recommendedProductIds.some((id) =>
          cart.items.some((item) => item.product_id === id)
        );

        if (!hasRecommended) {
          return {
            shouldApply: true,
            message: bundle.message,
          };
        }
      }
    }

    return { shouldApply: false };
  }
}

/**
 * Free Shipping Threshold Rule
 * Shows progress toward free shipping
 */
export class FreeShippingRule implements CartRule {
  id = 'free-shipping';
  name = 'Free Shipping Progress';

  constructor(private threshold: number) {}

  evaluate(context: RuleContext): RuleResult {
    const { cart } = context;
    const remaining = this.threshold * 100 - cart.total_price;
    const progress = Math.min(
      (cart.total_price / (this.threshold * 100)) * 100,
      100
    );

    if (remaining <= 0) {
      return {
        shouldApply: true,
        message: 'You qualify for free shipping! 🎉',
      };
    }

    return {
      shouldApply: false,
      message: `Add $${(remaining / 100).toFixed(
        2
      )} more to qualify for free shipping`,
    };
  }

  getProgress(cart: ShopifyCart): number {
    return Math.min((cart.total_price / (this.threshold * 100)) * 100, 100);
  }
}

/**
 * Minimum Order Rule
 * Enforces minimum cart value
 */
export class MinimumOrderRule implements CartRule {
  id = 'minimum-order';
  name = 'Minimum Order Value';

  constructor(private minimum: number) {}

  evaluate(context: RuleContext): RuleResult {
    const { cart } = context;
    const meetsMinimum = cart.total_price >= this.minimum * 100;

    if (!meetsMinimum) {
      const remaining = this.minimum * 100 - cart.total_price;
      return {
        shouldApply: false,
        message: `Add $${(remaining / 100).toFixed(
          2
        )} more to reach the minimum order value`,
      };
    }

    return { shouldApply: true };
  }
}

/**
 * Quantity Discount Rule
 * Discount for buying X or more of a product
 */
export interface QuantityDiscountConfig {
  productId: number;
  minQuantity: number;
  discountPercent: number;
  message: string;
}

export class QuantityDiscountRule implements CartRule {
  id = 'quantity-discount';
  name = 'Quantity Discount';

  constructor(private configs: QuantityDiscountConfig[]) {}

  evaluate(context: RuleContext): RuleResult {
    const { cart } = context;

    for (const config of this.configs) {
      const item = cart.items.find(
        (item) => item.product_id === config.productId
      );

      if (item && item.quantity >= config.minQuantity) {
        return {
          shouldApply: true,
          message: config.message,
        };
      }
    }

    return { shouldApply: false };
  }
}

/**
 * Rule Engine
 * Evaluates all active rules
 */
export class RuleEngine {
  private rules: CartRule[] = [];

  addRule(rule: CartRule): void {
    this.rules.push(rule);
  }

  removeRule(ruleId: string): void {
    this.rules = this.rules.filter((rule) => rule.id !== ruleId);
  }

  async evaluateAll(context: RuleContext): Promise<Map<string, RuleResult>> {
    const results = new Map<string, RuleResult>();

    for (const rule of this.rules) {
      try {
        const result = await rule.evaluate(context);
        results.set(rule.id, result);
      } catch (error) {
        console.error(`Rule ${rule.id} evaluation failed:`, error);
      }
    }

    return results;
  }

  getRules(): CartRule[] {
    return [...this.rules];
  }
}
